import { Head, useForm, usePage } from "@inertiajs/react";
import ContactFormField from "@/components/marketing/contact-form-field";
import SectionHeading from "@/components/marketing/section-heading";
import PublicLayout from "@/layouts/public-layout";
import { siteLinks } from "@/data/marketing-site";

const emptyValues = {
  first_name: "",
  last_name: "",
  email: "",
  organization: "",
  phone: "",
  assessment_consent: false,
  marketing_consent: false
};

const fieldRows = [
  [
    { name: "first_name", label: "Fornavn", autoComplete: "given-name" },
    { name: "last_name", label: "Efternavn", autoComplete: "family-name" }
  ],
  [
    { name: "email", label: "E-mail", type: "email", autoComplete: "email" },
    { name: "organization", label: "Virksomhed", autoComplete: "organization" }
  ],
  [{ name: "phone", label: "Telefon", type: "tel", autoComplete: "tel" }]
];

const consents = [
  { name: "assessment_consent", label: "Når du bestiller en NIS2-vurdering", required: true },
  { name: "marketing_consent", label: "Valgfrit samtykke", required: false }
];

function Nis2Report() {
  const { props } = usePage();
  const { contact_form: contactForm, flash } = props;
  const initialValues = Object.fromEntries(
    Object.entries(emptyValues).map(([name, value]) => [name, contactForm.values[name] ?? value])
  );
  const { data, setData, post, processing, errors, transform } = useForm(initialValues);
  const fieldError = (name) => errors[name] || contactForm.errors[name];

  return (
    <>
      <Head title="Gratis NIS2-vurdering">
        <meta head-key="description" name="description" content="Bestil en gratis og uforpligtende NIS2-vurdering hos Obsero." />
      </Head>

      <section className="public-section public-section--shell-band">
        <div className="public-container">
          <SectionHeading
            headingAs="h1"
            title="Bestil din gratis NIS2-vurdering"
            copy="Udfyld formularen, så kontakter vi dig om din NIS2-vurdering. Vurderingen er gratis og uforpligtende."
            className="public-section__header--centered"
          />

          <div className="public-contact-grid public-contact-grid--single">
            <div className="public-contact-card">
              <form
                className="public-contact-form public-contact-form--flush"
                autoComplete="on"
                onSubmit={(event) => {
                  event.preventDefault();
                  transform((values) => ({ nis2_report_request: values }));
                  post(siteLinks.nis2Report, {
                    preserveScroll: true,
                    onSuccess: () => setData({ ...emptyValues })
                  });
                }}
              >
                {flash.notice ? <div className="public-contact-form__success" role="status">{flash.notice}</div> : null}
                <p className="public-contact-form__note">Alle felter skal udfyldes.</p>

                {fieldRows.map((row, index) => (
                  <div key={index} className="public-contact-form__row">
                    {row.map(({ name, label, type = "text", autoComplete }) => (
                      <ContactFormField
                        key={name}
                        id={`nis2-report-${name}`}
                        name={name}
                        label={label}
                        type={type}
                        autoComplete={autoComplete}
                        value={data[name]}
                        onChange={(event) => setData(name, event.target.value)}
                        error={fieldError(name)}
                        required
                      />
                    ))}
                  </div>
                ))}

                {consents.map(({ name, label, required }) => {
                  const id = `nis2-report-${name}`;
                  const error = fieldError(name);

                  return (
                    <div key={name} className="form-check">
                      <input
                        id={id}
                        name={name}
                        type="checkbox"
                        className="form-check-input"
                        checked={data[name]}
                        onChange={(event) => setData(name, event.target.checked)}
                        required={required}
                        aria-invalid={Boolean(error)}
                        aria-describedby={error ? `${id}-error` : undefined}
                      />
                      <label htmlFor={id} className="form-check-label">
                        <span className="public-contact-form__label d-block mb-1">{label}{required ? " (obligatorisk)" : ""}</span>
                        <span className="public-contact-form__note d-block">{contactForm.consent_texts[name]}</span>
                      </label>
                      {error ? <div id={`${id}-error`} className="public-contact-form__error">{Array.isArray(error) ? error.join(" ") : error}</div> : null}
                    </div>
                  );
                })}

                <div className="public-contact-form__actions">
                  <button type="submit" className="btn btn-primary" disabled={processing}>
                    {processing ? "Sender..." : "Bestil gratis NIS2-vurdering"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

Nis2Report.layout = (page) => <PublicLayout>{page}</PublicLayout>;

export default Nis2Report;
